import type { Entry } from 'contentful'

import type {
  IPageStoriesFields,
  IPageStoryFields,
  IStoryTopicFields,
} from 'contentful-shared'

import { getEntriesWrapper } from '../../services/contentfulClient'

function shuffle<T>(a: T[]) {
  let j: number
  let x: T
  let i: number
  for (i = a.length - 1; i > 0; i--) {
    j = Math.floor(Math.random() * (i + 1))
    x = a[i]
    a[i] = a[j]
    a[j] = x
  }
  return a
}

type Slug = string | string[]
type Locale = string

export const fetchStoryBySlug = async (
  slug: string | string[],
  locale: string
) => {
  const entries = await getEntriesWrapper<IPageStoryFields>({
    content_type: 'pageStory',
    'fields.slug': slug,
    'fields.publishDate[lte]': new Date(),
    include: 5,
    'fields.locale[all]': locale,
    locale,
  })
  return entries.items[0]
}

export const fetchRecentStories = async (locale: Locale) => {
  const entries = await getEntriesWrapper<IPageStoryFields>({
    content_type: 'pageStory',
    order: '-fields.publishDate',
    'fields.publishDate[lte]': new Date(),
    include: 5,
    'fields.locale[all]': locale,
    locale,
  })
  return entries.items
}

export const fetchMoreStories = async (slug: Slug, locale: Locale) => {
  const pageData = await getEntriesWrapper<IPageStoryFields>({
    content_type: 'pageStory',
    'fields.slug[nin]': slug,
    'fields.publishDate[lte]': new Date(),
    include: 5,
    'fields.locale[all]': locale,
    locale,
  })
  if (pageData.items.length > 1) {
    const shuffled = shuffle(pageData.items)
    return shuffled.slice(0, 6)
  } else if (pageData.items.length === 1) {
    return pageData.items
  } else {
    return []
  }
}

export const fetchStoryTopicBySlug = async (slug: Slug) => {
  const entries = await getEntriesWrapper<IStoryTopicFields>({
    content_type: 'storyTopic',
    include: 0,
    'fields.topicSlug': slug,
  })
  return entries.items[0]
}

export const fetchStoryTagBySlug = async (slug: Slug) => {
  const entries = await getEntriesWrapper<IStoryTopicFields>({
    content_type: 'storyTag',
    include: 0,
    'fields.slug': slug,
  })
  return entries.items[0]
}

export const fetchStoriesPage = async (locale: Locale) => {
  const entries = await getEntriesWrapper<IPageStoriesFields>({
    content_type: 'pageStories',
    include: 2,
    locale,
  })
  return entries.items[0]
}

export const fetchStoriesThatLinkToEntity = async (
  entity: Entry<IStoryTopicFields>,
  locale: Locale
) => {
  const entries = await getEntriesWrapper<IPageStoryFields>({
    content_type: 'pageStory',
    order: '-fields.publishDate',
    'fields.publishDate[lte]': new Date(),
    include: 5,
    'fields.locale[all]': locale,
    locale,
    links_to_entry: entity.sys.id,
  })
  return entries.items
}
